import { useState, type ReactNode } from 'react';
import { otherLang, t as translate, useT, type DictionaryKey } from '@dia/i18n';
import {
  ageSeconds,
  freshnessOf,
  latestWater,
  TANKS,
  WATER_STALE_HOURS,
  type HourBucket,
  type PowerStatus,
  type PowerView,
  type Projection,
  type Tank,
  type WaterEntry,
} from '@dia/db';
import { cn } from '@dia/ui';
import { Icon } from './lib/icons';
import { Card, Notice, Pill } from './lib/ui';
import { acSummary, type AcTile } from './lib/ac';
import { arrowScale, arrowWidth, flowOf, GEN_STOP_SOC, waterWord, type Flow } from './lib/flow';
import {
  chartScale,
  defaultHour,
  formatKwh,
  formatNumber,
  formatPower,
  hourText,
  isThin,
  isToday,
  lastRan,
  loadName,
  readingOf,
  type Reading,
} from './lib/power';
import { addDays, formatDay, formatTime, panamaDateOf } from './lib/time';

/**
 * Energia: the island's power on one phone screen.
 *
 * Pure view. It gets the document the server worked out (view), the water
 * entries (water), and the time (now), and draws. No fetching here, so the same
 * component is what the owner sees, what a test renders, and what the preview
 * build shows.
 *
 * Top to bottom: one plain sentence, the picture of where the power goes, the
 * air conditioners, the water, today and tomorrow, whether the island is
 * reporting, then the generator and the by-hour chart.
 *
 * Section 8 decides how it behaves when data is thin: every value shows its
 * age, a value older than 15 minutes is grey and says so, a value older than an
 * hour is replaced by "no signal from the island", and nothing is ever filled
 * in with a guess. Water is read by hand, so its own line is a day.
 */

type Props = {
  view: PowerView | null;
  now: number;
  loading?: boolean;
  onRetry?: () => void | Promise<void>;
  /** Water entries, the island's and the ones still waiting on this phone. Null while loading. */
  water?: WaterEntry[] | null;
  /** Queue one entry per tank given. Omit to show the water read-only. */
  onSaveWater?: (levels: Partial<Record<Tank, number>>) => void | Promise<void>;
};

export function EnergiaView({ view, now, loading = false, onRetry, water = null, onSaveWater }: Props) {
  const { t, lang } = useT();
  const dash = view?.dashboard ?? null;

  if (!dash) {
    return (
      <div className="flex flex-col gap-5">
        <Card>
          <h2 className="mb-1 text-[1.15em] font-semibold">
            {loading ? t('ops.power.loading') : t('ops.power.nothing_yet')}
          </h2>
          {!loading && (
            <>
              <p className="text-inkMuted">{t('ops.power.nothing_yet_hint')}</p>
              {onRetry && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="mt-3 min-h-tap rounded border border-line px-4 text-accent underline underline-offset-4"
                >
                  {t('ops.power.retry')}
                </button>
              )}
            </>
          )}
        </Card>
        {/* Water is written by hand and needs nothing from the island. */}
        <WaterCard water={water} now={now} onSave={onSaveWater} />
      </div>
    );
  }

  const s = dash.status;
  const skew = view?.skewMs ?? 0;
  const docAge = ageSeconds(dash.computedAt, now, skew);
  const docLevel = freshnessOf(docAge);
  const today = isToday(dash.computedAt, now, skew);
  const time = (iso: string) => formatTime(iso, lang);

  return (
    <div className="flex flex-col gap-5">
      {s.alert.open && s.alert.openedAt && docLevel !== 'silent' && (
        <Notice tone="danger">
          <span className="block font-semibold">{t('ops.power.alert_title')}</span>
          <span className="block">{t('ops.power.alert_body', { time: time(s.alert.openedAt) })}</span>
        </Notice>
      )}

      {view?.cached && view.fetchedAt && (
        <p className="text-inkMuted">{t('ops.power.cached', { time: time(new Date(view.fetchedAt).toISOString()) })}</p>
      )}

      <FlowCard s={s} now={now} skew={skew} docLevel={docLevel} onRefresh={onRetry} />
      <AcCard s={s} now={now} skew={skew} />
      <WaterCard water={water} now={now} onSave={onSaveWater} />
      <TodayCard s={s} now={now} skew={skew} today={today} docLevel={docLevel} computedAt={dash.computedAt} />
      <IslandCard s={s} now={now} skew={skew} />
      <GeneratorCard s={s} now={now} skew={skew} docLevel={docLevel} computedAt={dash.computedAt} />
      <HourlyCard s={s} now={now} skew={skew} today={today} docLevel={docLevel} computedAt={dash.computedAt} />
      <LoadsCard s={s} now={now} skew={skew} />
    </div>
  );
}

/* ------------------------------------------------------------------ */

/** The same phrase in the language that is not on screen. Tim reads English, the crew read Spanish. */
function useOther() {
  const { lang } = useT();
  return (key: DictionaryKey, vars?: Record<string, string | number>) => translate(key, otherLang(lang), vars);
}

/** A card heading: the current language, with the other one quietly beside it. */
function Title({ icon, k }: { icon: Parameters<typeof Icon>[0]['name']; k: DictionaryKey }) {
  const { t } = useT();
  const other = useOther();
  return (
    <h2 className="mb-2 flex flex-wrap items-center gap-x-2 text-[1.15em] font-semibold">
      <Icon name={icon} /> {t(k)}
      <span className="text-body font-normal text-inkMuted">{other(k)}</span>
    </h2>
  );
}

/* The picture ----------------------------------------------------------- */

/** A downward arrow whose thickness is the power. The number is printed beside it, never only drawn. */
function Arrow({ watts, scaleW, label }: { watts: number; scaleW: number; label: string }) {
  const { lang } = useT();
  const w = arrowWidth(watts, scaleW);
  const head = w + 12;
  return (
    <div className="flex flex-col items-center" role="img" aria-label={label}>
      <svg width={head + 4} height={46} viewBox={`0 0 ${head + 4} 46`} aria-hidden="true" className="text-accent">
        <rect x={(head + 4 - w) / 2} y={0} width={w} height={32} fill="currentColor" />
        <polygon points={`2,30 ${head + 2},30 ${(head + 4) / 2},45`} fill="currentColor" />
      </svg>
      <p className="font-bold">{formatPower(watts, lang)}</p>
    </div>
  );
}

function Box({
  name,
  big,
  grey,
  children,
}: {
  name: string;
  big?: ReactNode;
  grey?: boolean;
  children?: ReactNode;
}) {
  return (
    <div className={cn('min-w-0 rounded border border-line bg-surface p-3 text-center', grey && 'text-inkFaint')}>
      <p className="font-semibold">{name}</p>
      {big && <p className="text-[1.75rem] font-bold leading-tight">{big}</p>}
      {children}
    </div>
  );
}

function FlowCard({
  s,
  now,
  skew,
  docLevel,
  onRefresh,
}: {
  s: PowerStatus;
  now: number;
  skew: number;
  docLevel: 'fresh' | 'stale' | 'silent';
  onRefresh?: () => void | Promise<void>;
}) {
  const { t, lang } = useT();
  const other = useOther();
  const soc = readingOf(s.battery.soc, s.battery.ts, now, skew);
  const gInput = s.generator.ts ? ageSeconds(s.generator.ts, now, skew) : null;
  const genRunning = s.generator.state === 'running' && gInput !== null && freshnessOf(gInput) === 'fresh';
  const res = flowOf({ s, genRunning, nowMs: now, skewMs: skew });

  const pct = soc.value === null ? null : Math.max(0, Math.min(100, Math.round(soc.value)));
  const grey = soc.level === 'stale';
  const fill = pct === null ? '' : pct < 20 ? 'bg-danger' : pct < 40 ? 'bg-warn' : 'bg-ok';
  const startSoc = s.generator.startSoc ?? 10;
  const power = (w: number) => formatPower(w, lang);

  const six = s.projection.sixAm;
  // The 6 AM estimate has no generator in it, so it is not said while the generator runs.
  const showSix = res.ok && six.ok && docLevel === 'fresh' && !genRunning;

  return (
    <Card>
      <Title icon="bolt" k="ops.power.live_title" />

      {/* One plain sentence, in both languages. */}
      {res.ok ? (
        <>
          <p className="text-[1.2em] font-semibold leading-snug">
            {t(`ops.power.head.${res.flow.headline}`, { stop: GEN_STOP_SOC })}
          </p>
          <p className="text-inkMuted">{other(`ops.power.head.${res.flow.headline}`, { stop: GEN_STOP_SOC })}</p>
          {showSix && six.ok && <p className="mt-1">{t('ops.power.head.at_six', { pct: Math.round(six.soc) })}</p>}
        </>
      ) : (
        <>
          <p className="text-[1.2em] font-semibold leading-snug">
            {res.why === 'silent' ? t('ops.power.silent') : t('ops.power.head.none')}
          </p>
          <p className="text-inkMuted">{other(res.why === 'silent' ? 'ops.power.silent' : 'ops.power.head.none')}</p>
          {onRefresh && (
            <div className="mt-2">
              <RefreshPill onRefresh={onRefresh} />
            </div>
          )}
        </>
      )}

      {res.ok && <Picture flow={res.flow} s={s} pct={pct} grey={grey} fill={fill} startSoc={startSoc} power={power} />}

      {/* The battery on its own when the picture cannot be drawn. */}
      {!res.ok && pct !== null && (
        <p className={cn('mt-3 text-[1.5rem] font-bold', grey && 'text-inkFaint')}>
          {t('ops.power.name.battery')} {pct}%
        </p>
      )}
      <div className="mt-3">
        <AgeLine r={soc} />
      </div>
    </Card>
  );
}

function Picture({
  flow,
  s,
  pct,
  grey,
  fill,
  startSoc,
  power,
}: {
  flow: Flow;
  s: PowerStatus;
  pct: number | null;
  grey: boolean;
  fill: string;
  startSoc: number;
  power: (w: number) => string;
}) {
  const { t, lang } = useT();
  const gen = flow.top === 'generator';
  const night = flow.top === 'battery';
  const scale = arrowScale(flow);
  const batteryHelps = flow.batteryToHouseW ?? (night ? flow.houseW : 0);
  const fromTop = Math.max(0, flow.toHouseW - batteryHelps);
  const topName = gen ? t('ops.power.name.generator') : t('ops.power.name.sun');
  const topBig = gen
    ? flow.genW === null
      ? null
      : power(flow.genW)
    : night
      ? null
      : power(flow.sunW);
  const aria = (from: string, to: string, w: number) => t('ops.power.flow.arrow_aria', { from, to, value: power(w) });

  return (
    <div className="mt-4">
      <div className="mx-auto w-3/5 min-w-0">
        <Box name={topName} big={topBig}>
          {gen && flow.genW === null && <p className="text-inkMuted">{t('ops.power.flow.gen_no_figure')}</p>}
          {night && <p className="text-inkMuted">{t('ops.power.flow.night')}</p>}
        </Box>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex min-h-[4.5rem] justify-center pt-1">
          {fromTop >= 200 && (
            <Arrow
              watts={fromTop}
              scaleW={scale}
              label={aria(topName, t('ops.power.name.house'), fromTop)}
            />
          )}
        </div>
        <div className="flex min-h-[4.5rem] justify-center pt-1">
          {flow.toBatteryW !== null && (
            <Arrow
              watts={flow.toBatteryW}
              scaleW={scale}
              label={aria(topName, t('ops.power.name.battery'), flow.toBatteryW)}
            />
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Box name={t('ops.power.name.house')} big={power(flow.houseW)}>
          <p className="text-caption text-inkMuted">{t('ops.power.flow.house_uses')}</p>
        </Box>
        <Box name={t('ops.power.name.battery')} big={pct === null ? null : `${pct}%`} grey={grey}>
          {pct !== null && (
            <div
              role="img"
              aria-label={t('ops.power.battery_aria', { pct })}
              className="relative mt-1 h-5 w-full overflow-hidden rounded border border-line bg-surfaceDeep"
            >
              <div className={cn('h-full', grey ? 'bg-inkFaint' : fill)} style={{ width: `${pct}%` }} />
              <span className="absolute inset-y-0 w-0.5 bg-ink/60" style={{ left: `${startSoc}%` }} />
              <span className="absolute inset-y-0 w-0.5 bg-ink/60" style={{ left: `${GEN_STOP_SOC}%` }} />
            </div>
          )}
          <p className="mt-1">
            {flow.batteryW === null ? (
              <Pill tone="neutral">{t('ops.power.flow.battery_unknown')}</Pill>
            ) : flow.batteryW >= 200 ? (
              <Pill tone="ok">{t('ops.power.flow.charging', { value: power(flow.batteryW) })}</Pill>
            ) : flow.batteryW <= -200 ? (
              <Pill tone="warn">{t('ops.power.flow.draining', { value: power(-flow.batteryW) })}</Pill>
            ) : (
              <Pill tone="neutral">{t('ops.power.flow.steady')}</Pill>
            )}
          </p>
          {s.battery.kwhStored !== null && !grey && (
            <p className="mt-1 text-caption text-inkMuted">
              {t('ops.power.stored', {
                kwh: formatNumber(s.battery.kwhStored, lang),
                cap: formatNumber(s.battery.capacityKwh, lang, 0),
              })}
            </p>
          )}
        </Box>
      </div>

      <div className="mt-3 flex flex-col gap-1">
        {flow.extraSunW !== null && <p>{t('ops.power.flow.sun_helps', { value: power(flow.extraSunW) })}</p>}
        {flow.batteryToHouseW !== null && <p>{t('ops.power.flow.battery_helps', { value: power(flow.batteryToHouseW) })}</p>}
        {flow.lostW !== null && <p className="text-inkMuted">{t('ops.power.flow.lost', { value: power(flow.lostW) })}</p>}
        <p className="text-caption text-inkMuted">
          {t('ops.power.gen.stops_at', { start: startSoc, stop: GEN_STOP_SOC })}
        </p>
      </div>
    </div>
  );
}

/* The air conditioners ---------------------------------------------------- */

function AcCard({ s, now, skew }: { s: PowerStatus; now: number; skew: number }) {
  const { t, lang } = useT();
  // Each unit's mode was true when the island last reported. Old, it is greyed
  // and says so; past an hour it is not shown at all (Section 8).
  const age = s.ac ? readingOf(s.ac.cooling, s.ac.ts, now, skew) : null;
  const sum = age && age.level !== 'silent' ? acSummary(s.ac) : null;
  const grey = age?.level === 'stale';

  return (
    <Card>
      <Title icon="snow" k="ops.power.ac.title" />
      {age?.level === 'silent' ? (
        <AgeLine r={age} />
      ) : !sum ? (
        <p className="text-inkMuted">{t('ops.power.ac.none')}</p>
      ) : (
        <div className={cn(grey && 'text-inkFaint')}>
          <p className="font-semibold">
            {sum.known === sum.total
              ? t('ops.power.ac.count', { on: sum.on, total: sum.total })
              : t('ops.power.ac.count_partial', { on: sum.on, missing: sum.total - sum.known })}
          </p>
          {sum.allSleep && <p className="text-inkMuted">{t('ops.power.ac.all_sleep')}</p>}
          {(['hacienda', 'bungalow'] as const).map((house) => (
            <div key={house} className="mt-3">
              <p className="mb-1 font-medium text-inkMuted">{t(`ops.power.ac.${house}`)}</p>
              <ul className="m-0 grid list-none grid-cols-1 gap-2 p-0 min-[360px]:grid-cols-2">
                {sum.tiles
                  .filter((x) => x.house === house)
                  .map((x) => (
                    <AcTileView key={x.slug} tile={x} lang={lang} />
                  ))}
              </ul>
            </div>
          ))}
          {age && <div className="mt-3"><AgeLine r={age} /></div>}
          <p className="mt-3 text-caption text-inkMuted">{t('ops.power.ac.legend')}</p>
        </div>
      )}
    </Card>
  );
}

function AcTileView({ tile, lang }: { tile: AcTile; lang: 'es' | 'en' }) {
  const { t } = useT();
  const tone = tile.mode === 'cooling' ? 'info' : tile.mode === 'sleep' ? 'ok' : 'neutral';
  return (
    <li
      className={cn(
        'min-w-0 rounded border p-2',
        tile.mode === 'unknown' ? 'border-dashed border-line text-inkFaint' : 'border-line',
        tile.mode === 'off' && 'text-inkMuted',
      )}
    >
      <p className="font-medium">{lang === 'es' ? tile.es : tile.en}</p>
      <p className="mt-1 flex items-center gap-1">
        {tile.mode !== 'off' && tile.mode !== 'unknown' && <Icon name="snow" size={18} />}
        <Pill tone={tone}>{t(`ops.power.ac.${tile.mode === 'cooling' ? 'cooling' : tile.mode}`)}</Pill>
      </p>
    </li>
  );
}

/* Water, entered by hand ---------------------------------------------------- */

function waterWhen(iso: string, nowMs: number, t: ReturnType<typeof useT>['t'], lang: 'es' | 'en'): string {
  const day = panamaDateOf(Date.parse(iso));
  const time = formatTime(iso, lang);
  const todayDate = panamaDateOf(nowMs);
  if (day === todayDate) return t('ops.power.water.today_at', { time });
  if (day === addDays(todayDate, -1)) return t('ops.power.water.yesterday_at', { time });
  return t('ops.power.water.day_at', { date: formatDay(day, lang), time });
}

function QuarterBar({ quarters, grey, tone }: { quarters: number; grey: boolean; tone: string }) {
  return (
    <div className="flex h-4 w-full gap-0.5" aria-hidden="true">
      {[1, 2, 3, 4].map((n) => (
        <span
          key={n}
          className={cn('h-full flex-1 rounded-sm border border-line', n <= quarters ? (grey ? 'bg-inkFaint' : tone) : 'bg-surfaceDeep')}
        />
      ))}
    </div>
  );
}

function WaterCard({
  water,
  now,
  onSave,
}: {
  water: WaterEntry[] | null;
  now: number;
  onSave?: Props['onSaveWater'];
}) {
  const { t, lang } = useT();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<Partial<Record<Tank, number>>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<'saved' | 'none' | null>(null);
  const latest = latestWater(water ?? [], now);
  const anyEntry = TANKS.some((k) => latest[k] !== null);

  const save = async () => {
    if (Object.keys(draft).length === 0) {
      setNote('none');
      return;
    }
    setBusy(true);
    try {
      await onSave?.(draft);
      setDraft({});
      setEditing(false);
      setNote('saved');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <Title icon="drop" k="ops.power.water.title" />
      <p className="mb-2 text-caption text-inkMuted">{t('ops.power.water.hand')}</p>

      {!anyEntry && water !== null && <p className="mb-2 text-inkMuted">{t('ops.power.water.never')}</p>}

      <ul className="m-0 flex list-none flex-col gap-3 p-0">
        {TANKS.map((tank) => {
          const l = latest[tank];
          const old = l !== null && l.ageHours >= WATER_STALE_HOURS;
          const word = l ? waterWord(l.quarters) : null;
          const tone = word === 'empty' ? 'bg-danger' : word === 'low' ? 'bg-warn' : 'bg-ok';
          return (
            <li key={tank} className={cn(old && 'text-inkFaint')}>
              <p className="flex flex-wrap items-baseline justify-between gap-x-2">
                <span className="font-medium">{t(`ops.power.water.tank.${tank}`)}</span>
                {l ? (
                  <span className="flex items-center gap-2">
                    <span className="font-bold">{t('ops.power.water.of_four', { q: l.quarters })}</span>
                    {word === 'empty' && <Pill tone={old ? 'neutral' : 'danger'}>{t('ops.power.water.empty')}</Pill>}
                    {word === 'low' && <Pill tone={old ? 'neutral' : 'warn'}>{t('ops.power.water.low')}</Pill>}
                  </span>
                ) : (
                  <span className="text-inkMuted">{t('ops.power.water.no_entry')}</span>
                )}
              </p>
              <QuarterBar quarters={l?.quarters ?? 0} grey={old || !l} tone={tone} />
              {l && (
                <p className="text-caption text-inkMuted">
                  {t('ops.power.water.entered', { when: waterWhen(l.readAt, now, t, lang) })}
                  {l.pending && ` · ${t('ops.power.water.pending')}`}
                </p>
              )}
              {old && <p className="text-caption font-medium text-warn">{t('ops.power.water.old')}</p>}
            </li>
          );
        })}
      </ul>

      {note === 'saved' && !editing && <p className="mt-3 font-medium text-ok">{t('ops.power.water.saved')}</p>}

      {onSave && !editing && (
        <button
          type="button"
          onClick={() => {
            setNote(null);
            setEditing(true);
          }}
          className="mt-3 inline-flex min-h-tap items-center gap-2 rounded border border-line bg-surface px-4 font-medium text-accent"
        >
          <Icon name="drop" size={20} />
          {t('ops.power.water.enter')}
        </button>
      )}

      {onSave && editing && (
        <div className="mt-4 rounded border border-line p-3">
          <p className="mb-3 text-inkMuted">{t('ops.power.water.form_hint')}</p>
          {TANKS.map((tank) => (
            <fieldset key={tank} className="mb-3 min-w-0 border-0 p-0">
              <legend className="mb-1 font-medium">{t(`ops.power.water.tank.${tank}`)}</legend>
              <div className="grid grid-cols-5 gap-1">
                {[0, 1, 2, 3, 4].map((q) => {
                  const picked = draft[tank] === q;
                  return (
                    <button
                      key={q}
                      type="button"
                      aria-pressed={picked}
                      aria-label={t('ops.power.water.pick_aria', {
                        tank: t(`ops.power.water.tank.${tank}`),
                        q,
                      })}
                      onClick={() => {
                        setNote(null);
                        setDraft((d) => ({ ...d, [tank]: q }));
                      }}
                      className={cn(
                        'min-h-tap rounded border font-bold',
                        picked ? 'border-ink bg-ink text-bg' : 'border-line bg-surface text-ink',
                      )}
                    >
                      {q}
                    </button>
                  );
                })}
              </div>
            </fieldset>
          ))}
          {note === 'none' && <p className="mb-2 font-medium text-warn">{t('ops.power.water.save_none')}</p>}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={() => void save()}
              className="min-h-tap rounded bg-accent px-4 font-semibold text-white disabled:opacity-70"
            >
              {t('ops.power.water.save')}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setDraft({});
                setNote(null);
                setEditing(false);
              }}
              className="min-h-tap rounded border border-line px-4"
            >
              {t('ops.power.water.cancel')}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}

/* Today, tomorrow, the battery later ----------------------------------------- */

function TodayCard({
  s,
  now,
  skew,
  today,
  docLevel,
  computedAt,
}: {
  s: PowerStatus;
  now: number;
  skew: number;
  today: boolean;
  docLevel: 'fresh' | 'stale' | 'silent';
  computedAt: string;
}) {
  const { t, lang } = useT();
  void now;
  void skew;
  const { sunset, sixAm, basis } = s.projection;
  const grey = docLevel === 'stale';
  const tomorrow = s.forecast.tomorrowKwh;

  const total = (label: string, v: PowerStatus['totals']['pvKwh']) => (
    <div className="min-w-0">
      <p className="text-inkMuted">{label}</p>
      {v && today && docLevel !== 'silent' ? (
        <>
          <p className={cn('text-[1.75rem] font-bold leading-tight', grey && 'text-inkFaint')}>
            {v.partial ? t('ops.power.at_least', { value: formatKwh(v.value, lang) }) : formatKwh(v.value, lang)}
          </p>
          <p className="text-caption text-inkMuted">
            {v.source === 'inverter' ? t('ops.power.from_inverter') : t('ops.power.counted_by_app')}
            {grey && ` · ${t('ops.power.data_until', { time: formatTime(computedAt, lang) })}`}
          </p>
        </>
      ) : (
        <p className="font-semibold">{v && today ? t('ops.power.silent') : t('ops.power.today.no_value')}</p>
      )}
    </div>
  );

  return (
    <Card>
      <Title icon="sun" k="ops.power.today.title" />
      <div className="grid grid-cols-1 gap-4 min-[360px]:grid-cols-2">
        {total(t('ops.power.today.sun_made'), s.totals.pvKwh)}
        {total(t('ops.power.today.house_used'), s.totals.loadKwh)}
      </div>

      <div className="mt-4">
        <p className="text-inkMuted">{t('ops.power.today.tomorrow')}</p>
        {tomorrow === null ? (
          <p className="font-semibold">{t('ops.power.today.no_value')}</p>
        ) : (
          <>
            <p className="text-[1.75rem] font-bold leading-tight">
              {t('ops.power.today.tomorrow_value', { value: formatKwh(tomorrow, lang) })}
            </p>
            <p className="text-caption text-inkMuted">{t('ops.power.today.forecast_note')}</p>
          </>
        )}
      </div>

      {docLevel === 'silent' ? (
        <div className="mt-4">
          <p className="font-semibold">{t('ops.power.silent')}</p>
          <p className="text-inkMuted">{t('ops.power.later_old', { time: formatTime(computedAt, lang) })}</p>
        </div>
      ) : (
        <>
          <div className="mt-4 flex flex-col gap-3">
            {sunset && <ProjectionRow label={t('ops.power.today.at_sunset')} p={sunset} grey={grey} />}
            <ProjectionRow label={t('ops.power.today.at_six')} p={sixAm} grey={grey} />
          </div>
          {grey && <p className="mt-2 text-inkMuted">{t('ops.power.later_old', { time: formatTime(computedAt, lang) })}</p>}
          <p className="mt-3 font-medium">{t('ops.power.estimate')}</p>
          {basis.loadW !== null && sixAm.ok && (
            <p className="text-inkMuted">
              {t('ops.power.basis', {
                load: formatPower(basis.loadW, lang),
                sun: s.forecast.remainingKwh !== null ? formatKwh(s.forecast.remainingKwh, lang) : '0 kWh',
              })}{' '}
              {basis.nightLoadSource === 'history' && basis.nightLoadW !== null
                ? t('ops.power.basis_night_history', { load: formatPower(basis.nightLoadW, lang) })
                : t('ops.power.basis_night_recent')}
            </p>
          )}
        </>
      )}
    </Card>
  );
}

/* Is the island reporting ---------------------------------------------------- */

function IslandCard({ s, now, skew }: { s: PowerStatus; now: number; skew: number }) {
  const { t, lang } = useT();
  const newest = [s.solar.ts, s.load.ts, s.battery.ts]
    .filter((x): x is string => x !== null)
    .sort()
    .pop();
  const level = newest ? freshnessOf(ageSeconds(newest, now, skew)) : 'silent';

  return (
    <Card>
      <p className="flex flex-wrap items-center gap-2 font-semibold">
        <Icon name="wifi" />
        {level === 'fresh' && t('ops.power.island.online')}
        {level === 'stale' && newest && t('ops.power.island.late', { time: formatTime(newest, lang) })}
        {level === 'silent' && t('ops.power.island.silent')}
      </p>
      {newest && level === 'fresh' && (
        <p className="text-caption text-inkFaint">{t('ops.power.updated_at', { time: formatTime(newest, lang) })}</p>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */

function H2({ icon, children }: { icon: Parameters<typeof Icon>[0]['name']; children: ReactNode }) {
  return (
    <h2 className="mb-2 flex items-center gap-2 text-[1.15em] font-semibold">
      <Icon name={icon} /> {children}
    </h2>
  );
}

/** "hace 3 min" for a reading, in words the crew say. */
function useAgeWords() {
  const { t } = useT();
  return (ageS: number) => {
    if (ageS < 90) return t('ops.power.age_now');
    if (ageS < 3600) return t('ops.power.age_min', { count: Math.round(ageS / 60) });
    return t('ops.power.age_hours', { count: Math.round(ageS / 3600) });
  };
}

/** Why a reading is not a number, or how old it is, in one line. Null when it is fresh. */
function AgeLine({ r }: { r: Reading }) {
  const { t, lang } = useT();
  const words = useAgeWords();
  if (r.level === 'none') return <p className="text-inkMuted">{t('ops.power.no_value')}</p>;
  if (r.level === 'silent') {
    return (
      <>
        <p className="font-semibold">{t('ops.power.silent')}</p>
        <p className="text-inkMuted">{t('ops.power.silent_hint', { time: formatTime(r.ts!, lang) })}</p>
      </>
    );
  }
  if (r.level === 'stale') {
    return (
      <p className="text-inkMuted">
        {t('ops.power.stale_since', { time: formatTime(r.ts!, lang) })} ({words(r.ageS!)})
      </p>
    );
  }
  return <p className="text-inkFaint">{t('ops.power.updated_at', { time: formatTime(r.ts!, lang) })}</p>;
}

/**
 * The calm way to say "this number is old": a quiet pill that fetches the latest
 * reading when tapped. The age line under the number stays, so how old it is
 * never goes missing. The word is paired with the icon (Section 9).
 */
function RefreshPill({ onRefresh }: { onRefresh: () => void | Promise<void> }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);
  return (
    <button
      type="button"
      disabled={busy}
      aria-busy={busy}
      onClick={() => {
        setBusy(true);
        void Promise.resolve(onRefresh()).finally(() => setBusy(false));
      }}
      className="inline-flex min-h-tap max-w-full items-center gap-2 rounded border border-line bg-surface px-3 py-1 text-body font-medium text-inkMuted disabled:opacity-70"
    >
      <span className={busy ? 'motion-safe:animate-spin' : undefined}>
        <Icon name="refresh" size={20} />
      </span>
      {t('ops.power.refresh')}
    </button>
  );
}

function ProjectionRow({
  label,
  p,
  grey,
}: {
  label: string;
  p: Projection;
  grey: boolean;
}) {
  const { t, lang } = useT();
  if (!p.ok) {
    return (
      <div className="rounded border border-line p-3">
        <p className="font-medium">{label}</p>
        <p className="text-inkMuted">{t(`ops.power.why.${p.reason}`)}</p>
      </div>
    );
  }
  const pct = Math.round(p.soc);
  const tone = pct < 20 ? 'danger' : pct < 40 ? 'warn' : 'ok';
  const word = pct < 20 ? t('ops.power.level.very_low') : pct < 40 ? t('ops.power.level.low') : t('ops.power.level.ok');
  return (
    <div className="rounded border border-line p-3">
      <p className="font-medium">{label}</p>
      <p className={cn('flex flex-wrap items-baseline gap-2', grey && 'text-inkFaint')}>
        <span className="text-[2rem] font-bold leading-tight">{pct}%</span>
        <Pill tone={grey ? 'neutral' : tone}>{word}</Pill>
      </p>
      {p.genStartsAt && <p className="text-warn">{t('ops.power.gen_would_start', { time: formatTime(p.genStartsAt, lang) })}</p>}
    </div>
  );
}

/* Use by the hour ---------------------------------------------------- */

function HourlyCard({
  s,
  now,
  skew,
  today,
  docLevel,
  computedAt,
}: {
  s: PowerStatus;
  now: number;
  skew: number;
  today: boolean;
  docLevel: 'fresh' | 'stale' | 'silent';
  computedAt: string;
}) {
  const { t, lang } = useT();
  const { ceilingW, bars } = chartScale(s.hourly, now, skew);
  const [picked, setPicked] = useState<number | null>(null);
  const hour = picked ?? defaultHour(s.hourly, now, skew);
  const bucket = s.hourly[hour] as HourBucket;
  const hasAny = s.hourly.some((b) => b.loadW !== null);

  if (!today) {
    return (
      <Card>
        <H2 icon="book">{t('ops.power.by_hour')}</H2>
        <p className="text-inkMuted">{t('ops.power.day_old')}</p>
      </Card>
    );
  }

  const loads = Object.entries(bucket.loads).sort((a, b) => b[1] - a[1]);

  return (
    <Card>
      <H2 icon="book">{t('ops.power.by_hour')}</H2>
      {!hasAny ? (
        <p className="text-inkMuted">{t('ops.power.hour_none')}</p>
      ) : (
        <>
          <p className="mb-2 text-inkMuted">{t('ops.power.by_hour_hint')}</p>
          {docLevel !== 'fresh' && (
            <p className="mb-2 font-medium text-warn">{t('ops.power.data_until', { time: formatTime(computedAt, lang) })}</p>
          )}
          <div role="group" aria-label={t('ops.power.chart_label')} className="flex h-36 items-end gap-px border-b border-line">
            {bars.map((b) => {
              const selected = b.hour === hour;
              const label = `${hourText(b.hour, lang)}: ${
                s.hourly[b.hour]!.loadW === null ? t('ops.power.hour_none') : formatPower(s.hourly[b.hour]!.loadW!, lang)
              }`;
              return (
                <button
                  key={b.hour}
                  type="button"
                  aria-label={label}
                  aria-pressed={selected}
                  onClick={() => setPicked(b.hour)}
                  className={cn(
                    'flex h-full min-w-0 flex-1 flex-col justify-end rounded-t outline-offset-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent',
                    selected && 'bg-accent/15',
                  )}
                >
                  {b.fraction !== null && (
                    <span
                      className={cn(
                        'block w-full rounded-t-sm',
                        b.thin ? 'bg-accent/40' : 'bg-accent',
                        selected && 'ring-2 ring-ink',
                      )}
                      style={{ height: `${Math.max(3, b.fraction * 100)}%` }}
                    />
                  )}
                </button>
              );
            })}
          </div>
          {/* Each label sits under its own hour: bar h spans h/24 to (h+1)/24 of the
              width. Only midnight and noon are labelled. Four labels ran into each
              other at 200 percent zoom, and two honest ones beat four crowded. */}
          <div className="relative mt-1 h-7 text-caption text-inkMuted" aria-hidden="true">
            {[0, 12].map((h) => (
              <span
                key={h}
                className={cn(
                  'absolute top-0 whitespace-nowrap',
                  // Under 300 px (200 percent zoom) the midnight label runs into
                  // the noon one, measured at 195 and 240 px.
                  h === 0 ? 'left-0 hidden min-[300px]:block' : '-translate-x-1/2',
                )}
                style={h === 0 ? undefined : { left: `${((h + 0.5) / 24) * 100}%` }}
              >
                {hourText(h, lang)}
              </span>
            ))}
          </div>
          <p className="text-caption text-inkMuted">{t('ops.power.chart_scale', { value: formatPower(ceilingW, lang) })}</p>

          <div className="mt-3 rounded border border-line p-3" aria-live="polite">
            <p className="text-center font-semibold">{t('ops.power.hour_at', { time: hourText(hour, lang) })}</p>
            <div className="mt-2 flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                aria-label={t('ops.power.hour_prev')}
                disabled={hour === 0}
                onClick={() => setPicked(Math.max(0, hour - 1))}
                className="grid min-h-tap min-w-tap place-items-center rounded border border-line disabled:opacity-40"
              >
                <span aria-hidden="true">&lsaquo;</span>
              </button>
              <button
                type="button"
                aria-label={t('ops.power.hour_next')}
                disabled={hour === 23}
                onClick={() => setPicked(Math.min(23, hour + 1))}
                className="grid min-h-tap min-w-tap place-items-center rounded border border-line disabled:opacity-40"
              >
                <span aria-hidden="true">&rsaquo;</span>
              </button>
            </div>
            {bucket.loadW === null ? (
              <p className="mt-2 text-inkMuted">{t('ops.power.hour_none')}</p>
            ) : (
              <>
                <p className="mt-2">{t('ops.power.hour_house', { value: formatPower(bucket.loadW, lang) })}</p>
                {bucket.pvW !== null && <p>{t('ops.power.hour_solar', { value: formatPower(bucket.pvW, lang) })}</p>}
                {loads.slice(0, 3).map(([slug, w]) => (
                  <p key={slug} className="text-inkMuted">
                    {t('ops.power.hour_load_line', { name: loadName(slug, lang), value: formatPower(w, lang) })}
                  </p>
                ))}
                {isThin(bucket, now, skew) && <p className="mt-1 text-warn">{t('ops.power.hour_thin')}</p>}
              </>
            )}
          </div>
        </>
      )}
    </Card>
  );
}

/* The biggest loads --------------------------------------------------- */

function LoadsCard({ s, now, skew }: { s: PowerStatus; now: number; skew: number }) {
  const { t, lang } = useT();
  // A measured load carries its own age too: the server dropped the stale ones
  // when it worked this out, but the phone may be holding that copy for a while.
  const loads = s.loads.filter((l) => readingOf(l.watts, l.ts, now, skew).level === 'fresh');

  return (
    <Card>
      <H2 icon="wrench">{t('ops.power.loads')}</H2>
      {loads.length === 0 ? (
        // Measured once but not heard from lately is a different thing from
        // never measured, and the screen must not say the second one.
        <p className="text-inkMuted">{s.loads.length > 0 ? t('ops.power.silent') : t('ops.power.loads_none')}</p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-3 p-0">
          {loads.map((l) => (
            <li key={l.slug}>
              <p className="flex items-baseline justify-between gap-2">
                <span className="font-medium">{loadName(l.slug, lang)}</span>
                <span className="font-bold">{formatPower(l.watts, lang)}</span>
              </p>
              {l.share !== null && (
                <>
                  <div className="mt-1 h-2 w-full rounded bg-surfaceDeep" aria-hidden="true">
                    <div className="h-full rounded bg-accent" style={{ width: `${Math.round(l.share * 100)}%` }} />
                  </div>
                  <p className="text-caption text-inkMuted">{t('ops.power.share', { pct: Math.round(l.share * 100) })}</p>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* The generator ------------------------------------------------------- */

function GeneratorCard({
  s,
  now,
  skew,
  docLevel,
  computedAt,
}: {
  s: PowerStatus;
  now: number;
  skew: number;
  docLevel: 'fresh' | 'stale' | 'silent';
  computedAt: string;
}) {
  const { t, lang } = useT();
  const g = s.generator;

  // The state was worked out from the readings when the island last reported.
  // A state from the last quarter hour is shown plainly. One that is a little
  // older is still shown, in a quiet colour, with the existing "data until"
  // line under it. Past an hour, or when there is no signal at all, nothing is
  // said about it: no warning, because there is nothing to warn about.
  const inputAge = g.ts ? ageSeconds(g.ts, now, skew) : null;
  const inputLevel = inputAge === null ? 'silent' : freshnessOf(inputAge);
  const state = g.state !== 'unknown' && inputLevel !== 'silent' ? g.state : 'unknown';
  const quiet = inputLevel !== 'fresh';

  const last = lastRan({
    lastRanAt: g.lastRanAt,
    documentState: g.state,
    state,
    trackingSince: g.trackingSince,
    nowMs: now,
    skewMs: skew,
  });
  const hasHistory = g.trackingSince !== null;

  const starts = (n: number) => (n === 1 ? t('ops.power.gen.starts_one') : t('ops.power.gen.starts', { count: n }));
  const daysAgo = (n: number) =>
    n === 0 ? t('ops.power.gen.today') : n === 1 ? t('ops.power.gen.yesterday') : t('ops.power.gen.days_ago', { count: n });

  return (
    <Card>
      <H2 icon="alert">{t('ops.power.generator')}</H2>
      {state !== 'unknown' && (
        <p className="mb-2">
          {state === 'running' ? (
            <Pill tone={quiet ? 'neutral' : 'warn'}>
              {g.runningSince
                ? t('ops.power.gen.running', { time: formatTime(g.runningSince, lang) })
                : t('ops.power.gen.running_now')}
            </Pill>
          ) : (
            <Pill tone={quiet ? 'neutral' : 'ok'}>{t('ops.power.gen.off')}</Pill>
          )}
        </p>
      )}
      {docLevel !== 'fresh' && hasHistory && (
        <p className="mb-2 font-medium text-warn">{t('ops.power.data_until', { time: formatTime(computedAt, lang) })}</p>
      )}

      {/* Hours and starts are counted from what the island has told us. With no
          generator data at all there is nothing to count, and a row of zeros
          would read as "it has never run". */}
      {hasHistory && (
      <div className="grid grid-cols-1 gap-3 min-[360px]:grid-cols-2">
        <div className="rounded border border-line p-3">
          <p className="font-medium">{t('ops.power.gen.week')}</p>
          <p>{t('ops.power.gen.hours', { hours: formatNumber(g.hoursWeek, lang) })}</p>
          <p className="text-inkMuted">{starts(g.startsWeek)}</p>
        </div>
        <div className="rounded border border-line p-3">
          <p className="font-medium">{t('ops.power.gen.month')}</p>
          <p>{t('ops.power.gen.hours', { hours: formatNumber(g.hoursMonth, lang) })}</p>
          <p className="text-inkMuted">{starts(g.startsMonth)}</p>
        </div>
      </div>
      )}

      <div className="mt-3">
        <p className="font-medium">{t('ops.power.gen.last_ran')}</p>
        {last.kind === 'days' ? (
          <p>{daysAgo(last.days)}</p>
        ) : last.kind === 'running_when_seen' ? (
          <p className="text-inkMuted">{t('ops.power.gen.running_when_seen')}</p>
        ) : last.kind === 'never_seen' ? (
          <p className="text-inkMuted">
            {t('ops.power.gen.never_seen', {
              date: formatDay(panamaDateOf(Date.parse(last.since)), lang),
            })}
          </p>
        ) : (
          <p className="text-inkMuted">{t('ops.power.no_value')}</p>
        )}
      </div>
    </Card>
  );
}
